"use client";

import { useEffect, useRef, useState } from "react";
import { Crop, X } from "lucide-react";

/**
 * "Crop to my yard" (PLA2-822). Photos shot from across the street are mostly
 * sky, roof and tree, leaving the planting zone as a thin band at the bottom.
 * Cropping before upload gives every outline the user draws several times more
 * room, which is the single biggest lever on output quality.
 */

export interface CropRect {
  x: number; // all fractions of the displayed image (0–1)
  y: number;
  w: number;
  h: number;
}

type DragMode = "move" | "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";

interface PhotoCropperProps {
  file: File;
  onCancel: () => void;
  onCropped: (file: File) => void;
  /** Crop suggested by the automatic photo check; opens pre-selected. */
  suggestedRect?: CropRect | null;
}

const FULL: CropRect = { x: 0, y: 0, w: 1, h: 1 };
const MIN_SIZE = 0.15;
// Drops roughly the top third, where sky and roofline usually sit.
const TRIM_SKY: CropRect = { x: 0, y: 0.35, w: 1, h: 0.65 };

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

function applyDrag(start: CropRect, mode: DragMode, dx: number, dy: number) {
  if (mode === "move") {
    return {
      ...start,
      x: clamp(start.x + dx, 0, 1 - start.w),
      y: clamp(start.y + dy, 0, 1 - start.h),
    };
  }
  let left = start.x;
  let top = start.y;
  let right = start.x + start.w;
  let bottom = start.y + start.h;
  if (mode.includes("w")) left = clamp(left + dx, 0, right - MIN_SIZE);
  if (mode.includes("e")) right = clamp(right + dx, left + MIN_SIZE, 1);
  if (mode.includes("n")) top = clamp(top + dy, 0, bottom - MIN_SIZE);
  if (mode.includes("s")) bottom = clamp(bottom + dy, top + MIN_SIZE, 1);
  return { x: left, y: top, w: right - left, h: bottom - top };
}

const HANDLES: { mode: DragMode; className: string }[] = [
  { mode: "nw", className: "-left-2 -top-2 cursor-nwse-resize" },
  { mode: "ne", className: "-right-2 -top-2 cursor-nesw-resize" },
  { mode: "sw", className: "-left-2 -bottom-2 cursor-nesw-resize" },
  { mode: "se", className: "-right-2 -bottom-2 cursor-nwse-resize" },
  {
    mode: "n",
    className: "left-1/2 -top-2 -translate-x-1/2 cursor-ns-resize",
  },
  {
    mode: "s",
    className: "left-1/2 -bottom-2 -translate-x-1/2 cursor-ns-resize",
  },
  {
    mode: "w",
    className: "-left-2 top-1/2 -translate-y-1/2 cursor-ew-resize",
  },
  {
    mode: "e",
    className: "-right-2 top-1/2 -translate-y-1/2 cursor-ew-resize",
  },
];

export function PhotoCropper({
  file,
  onCancel,
  onCropped,
  suggestedRect,
}: PhotoCropperProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [rect, setRect] = useState<CropRect>(suggestedRect ?? FULL);
  const [isSaving, setIsSaving] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    mode: DragMode;
    startX: number;
    startY: number;
    startRect: CropRect;
  } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const startDrag = (mode: DragMode) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startRect: rect,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    const frame = frameRef.current;
    if (!drag || !frame) return;
    const bounds = frame.getBoundingClientRect();
    const dx = (e.clientX - drag.startX) / bounds.width;
    const dy = (e.clientY - drag.startY) / bounds.height;
    setRect(applyDrag(drag.startRect, drag.mode, dx, dy));
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const rectStyle = {
    left: `${rect.x * 100}%`,
    top: `${rect.y * 100}%`,
    width: `${rect.w * 100}%`,
    height: `${rect.h * 100}%`,
  };

  const isFull = rect.x === 0 && rect.y === 0 && rect.w === 1 && rect.h === 1;

  const handleApply = () => {
    if (!src) return;
    if (isFull) {
      onCropped(file);
      return;
    }
    setIsSaving(true);
    const img = new Image();
    img.onload = () => {
      const sx = Math.round(rect.x * img.naturalWidth);
      const sy = Math.round(rect.y * img.naturalHeight);
      const sw = Math.max(1, Math.round(rect.w * img.naturalWidth));
      const sh = Math.max(1, Math.round(rect.h * img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = sw;
      canvas.height = sh;
      canvas.getContext("2d")!.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      canvas.toBlob(
        (blob) => {
          setIsSaving(false);
          if (!blob) {
            onCropped(file);
            return;
          }
          onCropped(
            new File([blob], file.name.replace(/\.\w+$/, "") + "-crop.jpg", {
              type: "image/jpeg",
            })
          );
        },
        "image/jpeg",
        0.92
      );
    };
    img.onerror = () => {
      setIsSaving(false);
      onCropped(file);
    };
    img.src = src;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-modal-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div className="relative w-full max-w-lg rounded-2xl border-2 border-plantista-950 dark:border-plantista-50 bg-white dark:bg-plantista-900 p-5 shadow-[6px_6px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 animate-in fade-in zoom-in duration-200">
        <button
          onClick={onCancel}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1 text-plantista-500 transition-colors hover:bg-plantista-100 dark:text-plantista-400 dark:hover:bg-plantista-800"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-3 pr-8">
          <h3
            id="crop-modal-title"
            className="flex items-center gap-2 font-display text-lg font-bold text-plantista-950 dark:text-white"
          >
            <Crop className="h-5 w-5 text-plantista-600 dark:text-plantista-300" />
            Crop to your yard
          </h3>
          <p className="mt-1 text-sm text-plantista-600 dark:text-plantista-300">
            Drag the edges so the area you want planted fills most of the frame.
            Trim away sky, roof and street.
          </p>
        </div>

        <div className="flex justify-center rounded-xl bg-plantista-950/5 dark:bg-black/30 p-2">
          {src && (
            <div
              ref={frameRef}
              className="relative inline-block touch-none select-none"
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <img
                src={src}
                alt="Your yard photo"
                draggable={false}
                className="block max-h-[55vh] w-auto max-w-full rounded-lg"
              />
              {/* Dim everything outside the crop. Clipped separately so the
                  handles below can sit on the frame edge without being cut. */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
                <div
                  className="absolute shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
                  style={rectStyle}
                />
              </div>
              <div
                onPointerDown={startDrag("move")}
                className="absolute cursor-move border-2 border-white"
                style={rectStyle}
              >
                {HANDLES.map(({ mode, className }) => (
                  <span
                    key={mode}
                    onPointerDown={startDrag(mode)}
                    className={`absolute h-4 w-4 rounded-full border-2 border-plantista-950 bg-plantista-400 ${className}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {suggestedRect && (
            <button
              type="button"
              onClick={() => setRect(suggestedRect)}
              className="rounded-full border border-plantista-300 dark:border-plantista-700 px-3 py-1 font-semibold text-plantista-700 dark:text-plantista-200 hover:bg-plantista-100 dark:hover:bg-plantista-800"
            >
              Suggested crop
            </button>
          )}
          <button
            type="button"
            onClick={() => setRect(TRIM_SKY)}
            className="rounded-full border border-plantista-300 dark:border-plantista-700 px-3 py-1 font-semibold text-plantista-700 dark:text-plantista-200 hover:bg-plantista-100 dark:hover:bg-plantista-800"
          >
            Trim the sky
          </button>
          <button
            type="button"
            onClick={() => setRect(FULL)}
            disabled={isFull}
            className="rounded-full border border-plantista-300 dark:border-plantista-700 px-3 py-1 font-semibold text-plantista-700 dark:text-plantista-200 hover:bg-plantista-100 dark:hover:bg-plantista-800 disabled:opacity-50"
          >
            Reset
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-2.5">
          <button
            onClick={handleApply}
            disabled={!src || isSaving}
            className="w-full rounded-lg border-2 border-plantista-950 bg-plantista-400 px-6 py-3 font-bold text-plantista-950 shadow-[4px_4px_0px_0px] shadow-plantista-950 transition-all hover:bg-plantista-500 active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px] disabled:opacity-60 dark:shadow-plantista-50"
          >
            {isSaving
              ? "Cropping…"
              : isFull
                ? "Use full photo"
                : "Use cropped photo"}
          </button>
          <button
            onClick={onCancel}
            className="w-full rounded-lg px-6 py-2 text-sm font-semibold text-plantista-600 transition-colors hover:bg-plantista-100 dark:text-plantista-300 dark:hover:bg-plantista-800"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
